'use client';

import { createContext, useContext } from 'react';

import { CreatorInfo } from '@ps/types/creator';

import type { ResolveSubscribeExpressIntent } from '../../types/SubscribeExpressIntent';

export interface SubscribeContextValue {
  creatorInfo: CreatorInfo;
  title: string;
  /** Close the modal (route back). */
  onClose: () => void;
  /** Called after a successful inline follow (free tier). */
  onFollowed?: () => void;
  /**
   * Optional post-success redirect for the paid-tier checkout (`redirect_to`).
   * Set only by the PDP subscription gate so the buyer returns to the product
   * after subscribing; every other caller omits it (keeps the checkout's default
   * success destination).
   */
  successRedirectUrl?: string;
  /**
   * Soft-navigate to `/checkout` (via `router.push`) instead of the default hard
   * `window.location.href`. Set only when the picker is mounted in a state modal
   * over the bio (the PDP `SubscribeDialog`): the caller closes this modal first
   * so it doesn't linger behind the `@checkout` intercept (the top modal layer),
   * keeping the opener page mounted underneath. Every route/account caller omits
   * it and keeps the app-wide hard-nav convention.
   */
  softCheckoutNav?: boolean;
  /**
   * Intercept-route soft checkout (bio `/<url_code>/subscribe`): on a paid-tier
   * confirm, SOFT-navigate to `/checkout` via `router.push` — WITHOUT calling
   * `onClose`. The isolated `@subscribe` slot keeps the picker mounted while the
   * bio stays underneath the `@checkout` intercept; `push` keeps `/subscribe` in
   * history so Back from checkout returns to the picker. 1:1 with the tipping
   * intercept (`useTipConfirm`). Mutually exclusive with {@link softCheckoutNav}
   * (the PDP state-modal path).
   */
  routeCheckoutNav?: boolean;
  /**
   * Resolves the express "Subscribe with Apple Pay" checkout intent for the
   * selected paid tier. Provided ONLY by the bio `/<url_code>/subscribe` route (a
   * server action) — when present, the picker shows the express button; other
   * callers (PDP `SubscribeDialog`, account) omit it, so no express button there.
   */
  resolveExpressIntent?: ResolveSubscribeExpressIntent;
}

export const SubscribeContext = createContext<SubscribeContextValue | null>(
  null,
);

export function useSubscribeContext(): SubscribeContextValue {
  const ctx = useContext(SubscribeContext);
  if (ctx == null) {
    throw new Error(
      'useSubscribeContext must be used inside a SubscribeProvider',
    );
  }
  return ctx;
}
